import {
  ExternalSourceSystem,
  ModernizedCourseContentSectionType,
  ModernizedQualityIndicator,
  ModernizedScoreCategory,
  type GetModernizedCourseQuery,
} from "@axio/graphql/generated/graphql";

import type {
  ActivityDetailMock,
  CourseEvaluatorReviewCategoryMock,
  CourseEvaluatorReviewDocumentIssueMock,
  CourseEvaluatorReviewEffectiveness,
  CourseEvaluatorReviewItemKind,
  CourseEvaluatorReviewMockData,
  CourseEvaluatorReviewSidebarGroupMock,
  CourseEvaluatorReviewSidebarItemMock,
  CourseEvaluatorReviewSidebarMeta,
  CourseEvaluatorReviewSidebarTag,
  CourseEvaluatorReviewItemType,
} from "./mockData/courseEvaluatorReview.mockData";

type ReviewCourse = NonNullable<GetModernizedCourseQuery["getModernizedCourse"]>;
type ReviewModule = ReviewCourse["modules"][number];
type ReviewAssignment = ReviewModule["assignments"][number];
type ReviewAssessment = ReviewModule["assessments"][number];
type ReviewAxioResource = ReviewModule["axioResources"][number];
type ReviewProject = ReviewModule["projects"][number];
type ReviewOtherActivity = ReviewModule["otherActivities"][number];
type ReviewIleRecommendation = ReviewModule["ileRecommendations"][number];
type ReviewContentSection = ReviewCourse["contentSections"][number];

export type ReviewDataBundle = {
  review: CourseEvaluatorReviewMockData;
  activityDetails: Record<string, ActivityDetailMock>;
};

const SCORE_CATEGORY_LABELS: Record<ModernizedScoreCategory, string> = {
  [ModernizedScoreCategory.CourseDesign]: "Course Design",
  [ModernizedScoreCategory.Assessment]: "Assessments",
  [ModernizedScoreCategory.Engagement]: "Engagement",
  [ModernizedScoreCategory.Accessibility]: "Accessibility",
};

const SOURCE_LABELS: Record<ExternalSourceSystem, string> = {
  [ExternalSourceSystem.Brightspace]: "Brightspace",
  [ExternalSourceSystem.Canvas]: "Canvas",
  [ExternalSourceSystem.Imscc]: "IMSCC",
  [ExternalSourceSystem.Pdf]: "PDF",
  [ExternalSourceSystem.Docx]: "DOCX",
};

const QUALITY_TO_EFFECTIVENESS: Record<ModernizedQualityIndicator, CourseEvaluatorReviewEffectiveness> = {
  [ModernizedQualityIndicator.Excellent]: "excellent",
  [ModernizedQualityIndicator.Good]: "good",
  [ModernizedQualityIndicator.Fair]: "fair",
  [ModernizedQualityIndicator.NeedsRevision]: "poor",
};

const CONTENT_SECTION_TITLES: Record<ModernizedCourseContentSectionType, string> = {
  [ModernizedCourseContentSectionType.LearningObjectives]: "Learning Objectives",
  [ModernizedCourseContentSectionType.CourseMaterialsList]: "Course Materials List",
  [ModernizedCourseContentSectionType.InstructorAvailability]: "Instructor Availability",
  [ModernizedCourseContentSectionType.Other]: "Other",
};

function mapActivityTypeToItemType(activityType: string | null | undefined): CourseEvaluatorReviewItemType {
  switch (activityType) {
    case "moduleDescription":
      return "moduleDescription";
    case "discussion":
      return "discussion";
    case "checklist":
      return "checklist";
    case "externalLink":
      return "externalLink";
    case "quiz":
      return "quiz";
    case "file":
      return "file";
    case "html-content":
      return "htmlContent";
    case "link":
      return "link";
    case "ile":
      return "ile";
    default:
      return "moduleDescription";
  }
}

function mapQualityToEffectiveness(
  quality: ModernizedQualityIndicator | null | undefined
): CourseEvaluatorReviewEffectiveness | undefined {
  if (!quality) return undefined;
  return QUALITY_TO_EFFECTIVENESS[quality];
}

function getLastModified(
  editLogs: Array<{ created?: string | null; actor?: { name?: string | null; username?: string | null } | null }>
) {
  const latest = [...editLogs].sort((left, right) => {
    const leftValue = left.created ? new Date(left.created).getTime() : 0;
    const rightValue = right.created ? new Date(right.created).getTime() : 0;
    return rightValue - leftValue;
  })[0];

  if (!latest) {
    return { lastModifiedBy: undefined, lastModifiedAt: undefined };
  }

  return {
    lastModifiedBy: latest.actor?.name ?? latest.actor?.username ?? "System",
    lastModifiedAt: latest.created
      ? new Date(latest.created).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
      : undefined,
  };
}

function getRecommendationSummary(
  recommendations: Array<{ recommendation?: string | null; rationale?: string | null }>
) {
  const primary = recommendations[0];
  return {
    feedback: primary?.rationale ?? undefined,
    recommendedDescription: primary?.recommendation ?? undefined,
  };
}

function buildMeta({
  itemType,
  isNew,
  qualityIndicator,
  tag,
}: {
  itemType?: CourseEvaluatorReviewItemType;
  isNew?: boolean | null;
  qualityIndicator?: ModernizedQualityIndicator | null;
  tag?: CourseEvaluatorReviewSidebarTag;
}): CourseEvaluatorReviewSidebarMeta[] | undefined {
  const meta: CourseEvaluatorReviewSidebarMeta[] = [];

  if (itemType && itemType !== "moduleDescription") {
    meta.push({ kind: "itemType", itemType });
  }

  if (tag) {
    meta.push({ kind: "tag", tag });
  }

  if (isNew) {
    meta.push({ kind: "tag", tag: "new" });
  }

  const effectiveness = mapQualityToEffectiveness(qualityIndicator);
  if (effectiveness) {
    meta.push({ kind: "effectiveness", effectiveness });
  }

  return meta.length > 0 ? meta : undefined;
}

function toCategoryBreakdown(course: ReviewCourse): CourseEvaluatorReviewCategoryMock[] {
  return course.categoryScores.map((score) => ({
    name: SCORE_CATEGORY_LABELS[score.category] ?? score.category,
    score: score.score ?? 0,
    maxScore: score.maxScore ?? 0,
  }));
}

function toDocumentIssues(course: ReviewCourse): CourseEvaluatorReviewDocumentIssueMock[] {
  return course.inaccessibleDocuments.map((doc) => ({
    id: doc.id,
    title: doc.title ?? "Unknown document",
    message: doc.errorReason ?? "Document could not be accessed",
    documentIssueKind: doc.errorReason?.toLowerCase().includes("authentication") ? "permissionDenied" : "brokenLink",
  }));
}

function addContentSectionDetail(details: Record<string, ActivityDetailMock>, section: ReviewContentSection): void {
  const recommendationSummary = getRecommendationSummary(section.recommendations);
  const lastModified = getLastModified(section.editLogs);

  details[section.id] = {
    id: section.id,
    currentTitle: "",
    currentDescription: section.valueText ?? section.valueHtml ?? "",
    recommendedTitle: CONTENT_SECTION_TITLES[section.type] ?? section.type,
    recommendedDescription: section.recommendedValue ?? recommendationSummary.recommendedDescription ?? "",
    activityType: "recommendation",
    feedback: recommendationSummary.feedback,
    documentReference: "Course Overview module",
    ...lastModified,
  };
}

function addModuleDescriptionItem(
  details: Record<string, ActivityDetailMock>,
  items: CourseEvaluatorReviewSidebarItemMock[],
  module: ReviewModule
): void {
  const itemId = `module-${module.id}`;

  items.push({
    id: itemId,
    title: module.title ?? "Untitled Module",
    kind: "moduleDescription",
    meta: buildMeta({ itemType: "moduleDescription", qualityIndicator: module.qualityIndicator }),
  });

  const lastModified = getLastModified(module.editLogs ?? []);

  details[itemId] = {
    id: itemId,
    currentTitle: module.title ?? "",
    currentDescription: module.description ?? "",
    recommendedTitle: module.recommendedTitle ?? module.title ?? "",
    recommendedDescription: module.recommendedDescription ?? "",
    activityType: "moduleDescription",
    feedback: module.recommendedDescription
      ? "Review and refine the generated module description before publishing."
      : undefined,
    documentReference: `Content > ${module.title ?? "Module"}`,
    ...lastModified,
  };
}

function addAssignmentItem(
  details: Record<string, ActivityDetailMock>,
  items: CourseEvaluatorReviewSidebarItemMock[],
  module: ReviewModule,
  assignment: ReviewAssignment
): void {
  const itemType = mapActivityTypeToItemType(assignment.gradingType);
  const tag =
    assignment.gradingType === "file" ? "lecture" : assignment.gradingType === "link" ? "supplementary" : undefined;
  const recommendationSummary = getRecommendationSummary(assignment.recommendations);
  const lastModified = getLastModified(assignment.editLogs);

  items.push({
    id: assignment.id,
    title: assignment.title ?? "Untitled Activity",
    kind: "assignment",
    meta: buildMeta({ itemType, isNew: assignment.isNew, qualityIndicator: assignment.qualityIndicator, tag }),
  });

  details[assignment.id] = {
    id: assignment.id,
    currentTitle: assignment.title ?? "",
    currentDescription: assignment.descriptionText ?? assignment.descriptionHtml ?? "",
    recommendedTitle: assignment.recommendedTitle ?? assignment.title ?? "",
    recommendedDescription:
      assignment.recommendedDescription ??
      recommendationSummary.recommendedDescription ??
      assignment.descriptionText ??
      "",
    activityType: assignment.gradingType ?? undefined,
    qualityLabel: assignment.qualityIndicator ?? undefined,
    pointsPossible: assignment.pointsPossible ?? undefined,
    dueDate: assignment.dueDate ? new Date(assignment.dueDate).toLocaleDateString("en-US") : undefined,
    url: assignment.externalUrl ?? undefined,
    feedback: recommendationSummary.feedback,
    documentReference: `Content > ${module.title ?? "Module"} > ${assignment.title ?? "Activity"}`,
    contentCategory: tag,
    ...lastModified,
  };
}

function addAssessmentItem(
  details: Record<string, ActivityDetailMock>,
  items: CourseEvaluatorReviewSidebarItemMock[],
  module: ReviewModule,
  assessment: ReviewAssessment
): void {
  const recommendationSummary = getRecommendationSummary(assessment.recommendations);
  const lastModified = getLastModified(assessment.editLogs);

  items.push({
    id: assessment.id,
    title: assessment.title ?? "Untitled Assessment",
    kind: "assignment",
    meta: buildMeta({ itemType: "quiz", isNew: assessment.isNew, qualityIndicator: assessment.qualityIndicator }),
  });

  details[assessment.id] = {
    id: assessment.id,
    currentTitle: assessment.title ?? "",
    currentDescription: "",
    recommendedTitle: assessment.title ?? "",
    recommendedDescription: recommendationSummary.recommendedDescription ?? "",
    activityType: "quiz",
    qualityLabel: assessment.qualityIndicator ?? undefined,
    pointsPossible: assessment.pointsPossible ?? undefined,
    dueDate: assessment.dueDate ? new Date(assessment.dueDate).toLocaleDateString("en-US") : undefined,
    url: assessment.externalUrl ?? undefined,
    feedback: recommendationSummary.feedback,
    documentReference: `Content > ${module.title ?? "Module"} > ${assessment.title ?? "Assessment"}`,
    ...lastModified,
  };
}

function addProjectItem(
  details: Record<string, ActivityDetailMock>,
  items: CourseEvaluatorReviewSidebarItemMock[],
  module: ReviewModule,
  project: ReviewProject
): void {
  const recommendationSummary = getRecommendationSummary(project.recommendations);
  const lastModified = getLastModified(project.editLogs);

  items.push({
    id: project.id,
    title: project.title ?? "Untitled Project",
    kind: project.isNew ? "suggestedContent" : "assignment",
    meta: buildMeta({ itemType: "htmlContent", isNew: project.isNew, qualityIndicator: project.qualityIndicator }),
  });

  details[project.id] = {
    id: project.id,
    currentTitle: project.title ?? "",
    currentDescription: project.descriptionText ?? project.descriptionHtml ?? "",
    recommendedTitle: project.recommendedTitle ?? project.title ?? "",
    recommendedDescription: project.recommendedDescription ?? recommendationSummary.recommendedDescription ?? "",
    activityType: "html-content",
    qualityLabel: project.qualityIndicator ?? undefined,
    dueDate: project.dueDate ? new Date(project.dueDate).toLocaleDateString("en-US") : undefined,
    url: project.externalUrl ?? undefined,
    feedback: recommendationSummary.feedback,
    documentReference: `Content > ${module.title ?? "Module"} > ${project.title ?? "Project"}`,
    ...lastModified,
  };
}

function addOtherActivityItem(
  details: Record<string, ActivityDetailMock>,
  items: CourseEvaluatorReviewSidebarItemMock[],
  module: ReviewModule,
  activity: ReviewOtherActivity
): void {
  const recommendationSummary = getRecommendationSummary(activity.recommendations);
  const lastModified = getLastModified(activity.editLogs);
  const itemType = mapActivityTypeToItemType(activity.activityType);
  const isSuggested = activity.activityType === "ile" || Boolean(activity.isNew);
  const kind: CourseEvaluatorReviewItemKind = isSuggested ? "suggestedContent" : "assignment";
  const recommendedDescription = activity.recommendedDescription ?? recommendationSummary.recommendedDescription ?? "";

  items.push({
    id: activity.id,
    title: activity.title ?? module.title ?? "Untitled Activity",
    kind,
    meta: buildMeta({ itemType, isNew: activity.isNew, qualityIndicator: activity.qualityIndicator }),
  });

  details[activity.id] = {
    id: activity.id,
    currentTitle: activity.title ?? "",
    currentDescription: activity.descriptionText ?? activity.descriptionHtml ?? "",
    recommendedTitle: activity.recommendedTitle ?? activity.title ?? "",
    recommendedDescription,
    activityType: activity.activityType ?? undefined,
    suggestedContentType: isSuggested ? (activity.activityType ?? undefined) : undefined,
    qualityLabel: activity.qualityIndicator ?? undefined,
    url: activity.externalUrl ?? undefined,
    feedback: recommendationSummary.feedback,
    learningObjective: activity.activityType === "ile" ? (recommendationSummary.feedback ?? undefined) : undefined,
    documentReference: `Content > ${module.title ?? "Module"}`,
    ...lastModified,
  };
}

function addAxioResourceItem(
  details: Record<string, ActivityDetailMock>,
  items: CourseEvaluatorReviewSidebarItemMock[],
  module: ReviewModule,
  resource: ReviewAxioResource
): void {
  const lastModified = getLastModified(resource.editLogs);

  items.push({
    id: resource.id,
    title: resource.title ?? "Untitled Resource",
    kind: "assignment",
    meta: buildMeta({ itemType: "link" }),
  });

  details[resource.id] = {
    id: resource.id,
    currentTitle: resource.title ?? "",
    currentDescription: resource.descriptionText ?? resource.descriptionHtml ?? "",
    recommendedTitle: resource.recommendedTitle ?? resource.title ?? "",
    recommendedDescription: resource.recommendedDescription ?? resource.descriptionText ?? "",
    activityType: "link",
    url: resource.externalUrl ?? undefined,
    documentReference: `Content > ${module.title ?? "Module"} > ${resource.title ?? "Resource"}`,
    ...lastModified,
  };
}

function addIleRecommendationItem(
  details: Record<string, ActivityDetailMock>,
  items: CourseEvaluatorReviewSidebarItemMock[],
  module: ReviewModule,
  recommendation: ReviewIleRecommendation
): void {
  const recommendationSummary = getRecommendationSummary(recommendation.recommendations);
  const lastModified = getLastModified(recommendation.editLogs);

  items.push({
    id: recommendation.id,
    title: recommendation.title ?? "Untitled ILE",
    kind: "suggestedContent",
    meta: buildMeta({ itemType: "ile", isNew: true }),
  });

  details[recommendation.id] = {
    id: recommendation.id,
    currentTitle: "",
    currentDescription: "",
    recommendedTitle: recommendation.title ?? "",
    recommendedDescription: recommendation.description ?? recommendationSummary.recommendedDescription ?? "",
    activityType: "ile",
    suggestedContentType: "ile",
    feedback: recommendationSummary.feedback,
    documentReference: `Content > ${module.title ?? "Module"}`,
    ...lastModified,
  };
}

function buildModuleGroups(
  course: ReviewCourse,
  details: Record<string, ActivityDetailMock>
): CourseEvaluatorReviewSidebarGroupMock[] {
  return course.modules.map((module) => {
    const items: CourseEvaluatorReviewSidebarItemMock[] = [];

    addModuleDescriptionItem(details, items, module);
    module.assignments.forEach((assignment) => addAssignmentItem(details, items, module, assignment));
    module.assessments.forEach((assessment) => addAssessmentItem(details, items, module, assessment));
    module.projects.forEach((project) => addProjectItem(details, items, module, project));
    module.otherActivities.forEach((activity) => addOtherActivityItem(details, items, module, activity));
    module.axioResources.forEach((resource) => addAxioResourceItem(details, items, module, resource));
    module.ileRecommendations.forEach((recommendation) =>
      addIleRecommendationItem(details, items, module, recommendation)
    );

    return {
      id: module.id,
      title: module.title ?? "Untitled Module",
      decided: 0,
      total: items.length,
      defaultExpanded: course.modules[0]?.id === module.id,
      items,
    };
  });
}

function buildCourseLevelGroup(
  course: ReviewCourse,
  details: Record<string, ActivityDetailMock>
): CourseEvaluatorReviewSidebarGroupMock {
  const items: CourseEvaluatorReviewSidebarItemMock[] = course.contentSections.map((section) => {
    addContentSectionDetail(details, section);
    return {
      id: section.id,
      title: CONTENT_SECTION_TITLES[section.type] ?? section.type,
      kind: "recommendation",
      meta: section.isNew ? [{ kind: "tag", tag: "new" }] : undefined,
    };
  });

  return {
    id: "course-level-content",
    title: "Course-Level Content",
    decided: 0,
    total: items.length,
    defaultExpanded: true,
    items,
  };
}

export function mapModernizedCourseToReviewData(course: ReviewCourse): ReviewDataBundle {
  const activityDetails: Record<string, ActivityDetailMock> = {};
  const courseLevelGroup = buildCourseLevelGroup(course, activityDetails);
  const moduleGroups = buildModuleGroups(course, activityDetails);
  const sidebarGroups = [courseLevelGroup, ...moduleGroups];
  const totalCount = sidebarGroups.reduce((sum, group) => sum + group.total, 0);

  return {
    review: {
      courseId: course.id,
      courseName: course.title ?? "",
      sourceLabel: course.externalSource ? SOURCE_LABELS[course.externalSource] : "Unknown",
      courseCode: course.courseCode ?? "",
      termLabel: course.term ?? "",
      frameworkName: "Dallas College Online Teaching Framework 3.1",
      reviewStage: "full-course-review",
      effectiveness: course.qualityRating ? QUALITY_TO_EFFECTIVENESS[course.qualityRating] : "good",
      reviewedDateLabel: course.modules[0]?.lastModified
        ? new Date(course.modules[0].lastModified).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          })
        : "",
      score: course.totalScore ?? 0,
      maxScore: course.totalMaxScore ?? 50,
      reviewedCount: 0,
      totalCount,
      queuedCount: 0,
      skippedCount: 0,
      categoryBreakdown: toCategoryBreakdown(course),
      strengths: [...course.strengths],
      improvements: [...course.itemsToImprove],
      documentIssues: toDocumentIssues(course),
      sidebarGroups,
    },
    activityDetails,
  };
}
